import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Events() {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState(null);
  const [createOpen, setCreateOpen] = useState(params.get("create") === "1");
  const [form, setForm] = useState({ title: "", description: "", organizer: "", date: "", time: "18:00", location: "", category: "fest" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([])), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title || !form.date || !form.location) return toast.error("Title, date and location are required");
    setBusy(true);
    try {
      await api.post("/events", form);
      toast.success("Event created!");
      setCreateOpen(false);
      setParams({});
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-3xl">Events</h1>
        <Button data-testid="create-event-btn" onClick={() => setCreateOpen(true)} className="rounded-full font-bold">
          <Plus className="w-4 h-4 mr-2" /> Create
        </Button>
      </div>

      {events === null ? (
        <p className="text-muted-foreground animate-pulse text-center py-10">Loading events…</p>
      ) : events.length ? (
        <div className="grid sm:grid-cols-2 gap-5">
          {events.map((e) => (
            <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}
              className="t-card p-5 hover:-translate-y-1 transition-transform duration-200">
              <div className="flex items-center justify-between">
                <span className="t-chip bg-primary/10 text-primary">{e.category}</span>
                {e.my_status && <span className="t-chip bg-secondary/20 text-[#14532D]">{e.my_status.replace("_", " ")}</span>}
              </div>
              <h2 className="font-display font-bold text-lg mt-3">{e.title}</h2>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date} · {e.time}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</p>
              <p className="mt-3 text-sm font-bold">{e.going} going · {e.maybe} maybe</p>
            </Link>
          ))}
        </div>
      ) : (
        <div className="t-card p-10 text-center" data-testid="empty-events">
          <p className="font-display font-bold">No events yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create the first event for your campus.</p>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Create event</DialogTitle></DialogHeader>
          <Input data-testid="event-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-2xl" />
          <Textarea data-testid="event-description-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl" />
          <Input data-testid="event-organizer-input" placeholder="Organizer (e.g. Cultural Club)" value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} className="rounded-2xl" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-2xl" />
            <Input data-testid="event-time-input" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-2xl" />
          </div>
          <Input data-testid="event-location-input" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-2xl" />
          <Input data-testid="event-category-input" placeholder="Category (fest, workshop, sports…)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-2xl" />
          <Button data-testid="event-submit-btn" disabled={busy} onClick={submit} className="rounded-full font-bold">{busy ? "Creating…" : "Create event"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
